import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { Loader } from "components/Loader";
import { PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { showToast } from "services/utils/Status";
import { StatutoryPage, StatutoryTabs } from "pages/PayRoll/Shared/statutoryUi";
import { ActiveVersionCard } from "pages/PayRoll/Shared/statutoryConfigUi";
import { NewRequestButton } from "pages/TeamZone/Approve/approveUi";
import { activeVersion } from "./esiConfigData";
import {
  createEsiDraftFrom,
  createEsiVersion,
  discardEsiDraft,
  fetchEmployerCodes,
  fetchEsiDefaults,
  fetchEsiOptions,
  fetchEsiStatus,
  fetchEsiVersions,
  fetchReasonCodes,
  updateEsiVersion,
} from "./esiConfigApi";
import { EsiSettingsTab } from "./EsiSettingsTab";
import { EsiVersionHistoryPanel } from "./EsiVersionHistoryPanel";
import { EsiEmployerCodesTab } from "./EsiEmployerCodesTab";
import { EsiReasonCodesTab } from "./EsiReasonCodesTab";
import { usePayrollSettingsCrumbs } from "../../payrollSettingsMenu";
import { OpenDailyScreenButton } from "pages/PayRoll/Shared/settingsSwitch";

// Payroll > Configuration > Statutory > ESI (screens C1–C5a): ESI settings
// with versions, employer codes and reason codes, all through the ESI API
// (esiConfigApi.js). The Employer Codes and Reason Codes tabs load the
// first time they are opened and keep their data while the user switches
// tabs.

export const ESI_CONFIG_TABS = [
  {
    value: "configuration",
    label: "Configuration",
    title: "ESI Configuration",
    subtitle: "Configure ESI eligibility, contribution periods and calculation components. Create a new version when there are changes.",
  },
  {
    value: "employer-codes",
    label: "Employer Codes",
    title: "ESI Employer Codes",
    subtitle: "The company's ESI employer code and any branch sub-codes. Employees get their code from their site.",
  },
  {
    value: "reason-codes",
    label: "Reason Codes",
    title: "ESI Reason Codes",
    subtitle: "Used in the ESIC file when an employee has 0 days paid in a month. Map your own exit and absence reasons once — the file picks the code automatically.",
  },
];

// Data of a tab that loads on first open: { data, loading, error, load, setData }.
const useTabData = (fetcher) => {
  const [state, setState] = useState({ data: null, loading: false, error: "" });
  const controller = useRef(null);

  const load = useCallback(async () => {
    controller.current?.abort();
    const request = new AbortController();
    controller.current = request;
    setState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const data = await fetcher(request.signal);
      setState({ data, loading: false, error: "" });
    } catch (err) {
      if (err.name === "AbortError") return;
      setState((current) => ({ ...current, loading: false, error: err.message || "Couldn't load this tab." }));
    }
  }, [fetcher]);

  useEffect(() => () => controller.current?.abort(), []);
  const setData = useCallback((data) => setState({ data, loading: false, error: "" }), []);
  return { ...state, load, setData };
};

// Error (with Retry), loading, or the tab itself.
const TabBody = ({ tab, label, children }) =>
  tab.error ? (
    <RequestInfoBanner
      tone="danger"
      title={`Couldn't load the ${label}.`}
      body={tab.error}
      action={<SecondaryButton onClick={tab.load} sx={{ whiteSpace: "nowrap" }}>Retry</SecondaryButton>}
    />
  ) : tab.data ? (
    children
  ) : (
    <Mui.Box role="status" aria-label={`Loading ${label}`}>
      <Loader info="Loading..." minHeight="40vh" />
    </Mui.Box>
  );

export const EsiConfiguration = ({ nav, initialTab }) => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  // Fresh data per company.
  return <EsiConfigurationScreen key={`${domain}:${topbarCompanyId}`} domain={domain} companyId={topbarCompanyId} nav={nav} initialTab={initialTab} />;
};

const EsiConfigurationScreen = ({ domain, companyId, nav, initialTab }) => {
  const settingsCrumbs = usePayrollSettingsCrumbs();
  const [tab, setTab] = useState(() => (ESI_CONFIG_TABS.some((item) => item.value === initialTab) ? initialTab : "configuration"));
  const [versions, setVersions] = useState([]);
  const [load, setLoad] = useState({ loading: true, error: "" });
  const [busy, setBusy] = useState(false);
  // The version shown in the Configuration tab (the active one by default).
  const [selectedId, setSelectedId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [addCodeRequest, setAddCodeRequest] = useState(0);
  const [options, setOptions] = useState(null);
  const [setupStatus, setSetupStatus] = useState(null);
  const controller = useRef(null);

  const codesTab = useTabData(useCallback((signal) => fetchEmployerCodes({ domain, companyId, signal }), [domain, companyId]));
  const reasonTab = useTabData(useCallback((signal) => fetchReasonCodes({ domain, companyId, signal }), [domain, companyId]));

  // First open of a tab loads it; switching back keeps what is loaded.
  const { data: codesData, loading: codesLoading, error: codesError, load: loadCodes } = codesTab;
  const { data: reasonData, loading: reasonLoading, error: reasonError, load: loadReasons } = reasonTab;
  useEffect(() => {
    if (tab === "employer-codes" && !codesData && !codesLoading && !codesError) loadCodes();
    if (tab === "reason-codes" && !reasonData && !reasonLoading && !reasonError) loadReasons();
  }, [tab, codesData, codesLoading, codesError, loadCodes, reasonData, reasonLoading, reasonError, loadReasons]);

  const current = ESI_CONFIG_TABS.find((item) => item.value === tab);
  const active = activeVersion(versions.filter((version) => version.status !== "Draft"));
  const draft = versions.find((version) => version.status === "Draft");

  // Setup checklist: payroll skips ESI while a check fails.
  const loadStatus = useCallback(() => {
    fetchEsiStatus({ domain, companyId })
      .then(setSetupStatus)
      .catch(() => setSetupStatus(null));
  }, [domain, companyId]);

  // (Re)load the versions and the draft; keeps the selection if it still
  // exists, else `selectId` (the version just saved) or the active version.
  const loadVersions = useCallback(
    async (selectId) => {
      controller.current?.abort();
      const request = new AbortController();
      controller.current = request;
      setLoad((state) => ({ ...state, loading: true, error: "" }));
      try {
        const list = await fetchEsiVersions({ domain, companyId, signal: request.signal });
        setVersions(list);
        setSelectedId((currentId) => {
          const wanted = selectId ?? currentId;
          if (list.some((version) => version.id === wanted)) return wanted;
          return activeVersion(list.filter((version) => version.status !== "Draft"))?.id ?? list[0]?.id ?? null;
        });
        setLoad({ loading: false, error: "" });
      } catch (err) {
        if (err.name === "AbortError") return;
        setLoad({ loading: false, error: err.message || "Couldn't load the ESI configuration." });
      }
    },
    [domain, companyId],
  );

  useEffect(() => {
    loadVersions();
    loadStatus();
    const request = new AbortController();
    fetchEsiOptions({ domain, companyId, signal: request.signal })
      .then(setOptions)
      .catch(() => {});
    return () => {
      controller.current?.abort();
      request.abort();
    };
  }, [loadVersions, loadStatus, domain, companyId]);

  // ESI not set up yet: a blank Version 1 from the server template.
  const setUpEsi = async () => {
    setBusy(true);
    try {
      const template = await fetchEsiDefaults({ domain, companyId });
      setVersions((list) => [...list, template]);
      setSelectedId(template.id);
    } catch (err) {
      showToast("error", err.message);
    } finally {
      setBusy(false);
    }
  };

  // Create New Version copies the active version into the company's draft;
  // Open Draft opens the draft that already exists.
  const createVersion = async () => {
    setTab("configuration");
    if (draft) {
      setSelectedId(draft.id);
      return;
    }
    const base = active || versions[versions.length - 1];
    if (!base) return;
    setBusy(true);
    try {
      const { version, message } = await createEsiDraftFrom({ domain, companyId, versionId: base.id });
      showToast("success", message || `${version.name} created as a draft.`);
      setVersions((list) => [...list.filter((item) => item.status !== "Draft"), version]);
      setSelectedId(version.id);
    } catch (err) {
      showToast("error", err.message);
    } finally {
      setBusy(false);
    }
  };

  // Save & Activate (draft) or Save (unlocked saved version). A 409 means
  // the version changed or got locked: reload so the screen shows it.
  const persist = {
    save: async (form, { isDraft }) => {
      try {
        const result = isDraft ? await createEsiVersion({ domain, companyId, form }) : await updateEsiVersion({ domain, companyId, form });
        if (isDraft && form.id !== "new") await discardEsiDraft({ domain, companyId }).catch(() => {});
        showToast("success", result.message || `${result.version.name} saved.`);
        await loadVersions(result.version.id);
        loadStatus();
      } catch (err) {
        if (err.status === 409) loadVersions(form.id);
        throw err;
      }
    },
    discard: async (form) => {
      if (form.id !== "new") await discardEsiDraft({ domain, companyId });
      showToast("success", "Draft discarded.");
      if (form.id === "new") {
        setVersions((list) => list.filter((version) => version.id !== "new"));
        setSelectedId(null);
      } else {
        await loadVersions(active?.id);
      }
    },
  };

  const failedChecks = (setupStatus?.checks || []).filter((check) => !check.ok);

  return (
    <StatutoryPage
      // Each crumb opens its screen; the last one is the current tab.
      breadcrumb={settingsCrumbs("Statutory", { label: "ESI", onClick: () => setTab("configuration") }, current.label)}
      title={current.title}
      switchAction={<OpenDailyScreenButton group="statutory" item="esi" />}
      subtitle={current.subtitle}
      nav={nav}
      aside={
        tab === "configuration" && active ? (
          <ActiveVersionCard version={active} hasDraft={Boolean(draft)} onHistory={() => setHistoryOpen(true)} onCreate={busy ? () => {} : createVersion} />
        ) : tab === "employer-codes" && codesTab.data ? (
          // Same header button as the Team Workspace Approve screens.
          <NewRequestButton hasAccess onClick={() => setAddCodeRequest((count) => count + 1)}>
            Add Code
          </NewRequestButton>
        ) : null
      }
    >
      <StatutoryTabs value={tab} onChange={setTab} tabs={ESI_CONFIG_TABS} />

      {tab === "configuration" ? (
        load.error ? (
          <RequestInfoBanner
            tone="danger"
            title="Couldn't load the ESI configuration."
            body={load.error}
            action={<SecondaryButton onClick={() => loadVersions()} sx={{ whiteSpace: "nowrap" }}>Retry</SecondaryButton>}
          />
        ) : load.loading && !versions.length ? (
          <Mui.Box role="status" aria-label="Loading ESI configuration">
            <Loader info="Loading..." minHeight="40vh" />
          </Mui.Box>
        ) : versions.length ? (
          <Mui.Stack spacing={2}>
            {setupStatus && setupStatus.ready === false && failedChecks.length && versions.some((version) => version.status !== "Draft") ? (
              <RequestInfoBanner
                tone="warning"
                title="ESI is not ready for payroll yet."
                body={`Payroll skips ESI until these are done: ${failedChecks.map((check) => check.label).join(" · ")}.`}
              />
            ) : null}
            <EsiSettingsTab
              domain={domain}
              companyId={companyId}
              versions={versions}
              setVersions={setVersions}
              selectedId={selectedId}
              setSelectedId={setSelectedId}
              options={options}
              persist={persist}
            />
          </Mui.Stack>
        ) : (
          <RequestInfoBanner
            title="ESI is not set up yet."
            body="Enter the wage ceiling, contribution periods and coverage rules to start."
            action={
              <PrimaryButton onClick={setUpEsi} loading={busy} sx={{ whiteSpace: "nowrap" }}>
                Set up ESI
              </PrimaryButton>
            }
          />
        )
      ) : null}
      {tab === "employer-codes" ? (
        <TabBody tab={codesTab} label="ESI employer codes">
          <EsiEmployerCodesTab domain={domain} companyId={companyId} data={codesTab.data} onReload={codesTab.load} addRequest={addCodeRequest} />
        </TabBody>
      ) : null}
      {tab === "reason-codes" ? (
        <TabBody tab={reasonTab} label="ESI reason codes">
          <EsiReasonCodesTab domain={domain} companyId={companyId} data={reasonTab.data} onChange={reasonTab.setData} />
        </TabBody>
      ) : null}

      <EsiVersionHistoryPanel
        open={historyOpen}
        versions={versions}
        selectedId={selectedId}
        onClose={() => setHistoryOpen(false)}
        onOpenVersion={(id) => {
          setSelectedId(id);
          setTab("configuration");
          setHistoryOpen(false);
        }}
      />
    </StatutoryPage>
  );
};
